import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { cereriAPI } from '../services/api';
import { Card, StatusBadge, PrioritateBadge, Spinner, EmptyState, PageHeader, Button } from '../components/common';
import { formatData, TIP_LABELS, truncate } from '../utils';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';
import { useWindowWidth } from '../hooks/useWindowWidth';

const STATUSURI = ['','inregistrata','in_analiza','in_lucru','rezolvata','respinsa','arhivata'];
const STATUS_LABELS_UI = { '':'Toate', inregistrata:'Înregistrată', in_analiza:'În analiză', in_lucru:'În lucru', rezolvata:'Rezolvată', respinsa:'Respinsă', arhivata:'Arhivată' };

export default function CereriListPage() {
  const { isFunctionar } = useAuth();
  const w = useWindowWidth();
  const isMobile = w < 640;
  const [searchParams] = useSearchParams();
  const statusInitial = searchParams.get('status') || '';
  const [filters, setFilters] = useState({ status: statusInitial, tip: '', page: 1 });
  const [search, setSearch]   = useState('');

  const { data, isLoading, isFetching } = useQuery(
    ['cereri', filters],
    () => cereriAPI.list({ ...filters, limit: 20 }).then(r => r.data),
    { keepPreviousData: true }
  );

  const cereri     = data?.data || [];
  const pagination = data?.pagination || {};

  function setFilter(key, value) {
    setFilters(f => ({ ...f, [key]: value, page: 1 }));
  }

  const filtrate = search
    ? cereri.filter(c =>
        c.titlu?.toLowerCase().includes(search.toLowerCase()) ||
        c.numar_cerere?.toLowerCase().includes(search.toLowerCase())
      )
    : cereri;

  return (
    <Layout>
      <PageHeader
        title={filters.status === 'arhivata' ? 'Arhivă' : 'Cereri'}
        subtitle={filters.status === 'arhivata'
          ? `${pagination.total || 0} cereri arhivate — deschide o cerere pentru a vedea atașamentele și documentele generate`
          : `${pagination.total || 0} cereri total`}
        action={<Link to="/cereri/noua"><Button>+ Cerere nouă</Button></Link>}
      />

      {/* Filtre */}
      <Card>
        <div style={s.filterRow}>
          <input
            type="text"
            placeholder="Caută după titlu sau număr..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={s.searchInput}
          />
          <select
            value={filters.status}
            onChange={e => setFilter('status', e.target.value)}
            style={s.select}
          >
            {STATUSURI.map(st => (
              <option key={st} value={st}>{STATUS_LABELS_UI[st]}</option>
            ))}
          </select>
          {isFunctionar && (
            <select
              value={filters.tip}
              onChange={e => setFilter('tip', e.target.value)}
              style={s.select}
            >
              <option value="">Toate tipurile</option>
              {Object.entries(TIP_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          )}
          {(filters.status || filters.tip || search) && (
            <button
              onClick={() => { setFilters({ status:'', tip:'', page:1 }); setSearch(''); }}
              style={s.clearBtn}
            >
              ✕ Resetează
            </button>
          )}
        </div>
      </Card>

      {/* Tabs status rapid */}
      <div style={s.tabs}>
        {STATUSURI.map(st => (
          <button
            key={st}
            onClick={() => setFilter('status', st)}
            style={{ ...s.tab, ...(filters.status === st ? s.tabActive : {}) }}
          >
            {STATUS_LABELS_UI[st]}
          </button>
        ))}
      </div>

      {/* Lista */}
      <Card>
        {isLoading
          ? <Spinner message="Se încarcă cererile..." />
          : filtrate.length === 0
            ? <EmptyState icon="📋" title="Nicio cerere găsită" text="Modifică filtrele sau depune o cerere nouă." action={<Link to="/cereri/noua"><Button>Cerere nouă</Button></Link>} />
            : (
              <div>
                {/* Header tabel — ascuns pe mobil */}
                {!isMobile && (
                  <div style={s.tableHeader}>
                    <span style={{...s.th, flex: 1.5}}>Număr</span>
                    <span style={{...s.th, flex: 4}}>Subiect</span>
                    <span style={{...s.th, flex: 1.5}}>Tip</span>
                    <span style={{...s.th, flex: 2}}>Status</span>
                    <span style={{...s.th, flex: 2}}>Data</span>
                    {isFunctionar && <span style={{...s.th, flex: 2}}>Solicitant</span>}
                  </div>
                )}

                {filtrate.map(c => isMobile ? (
                  <Link key={c.id} to={`/cereri/${c.id}`} style={s.card}>
                    <div style={s.cardTop}>
                      <span style={s.cardNumar}>{c.numar_cerere}</span>
                      <StatusBadge status={c.status} />
                    </div>
                    <div style={s.cardTitlu}>{c.titlu}</div>
                    <div style={s.cardMeta}>
                      <span>{TIP_LABELS[c.tip] || c.tip}</span>
                      <span>{formatData(c.created_at)}</span>
                    </div>
                    {isFunctionar && <div style={s.cardSolicitant}>{c.solicitant || '—'}</div>}
                  </Link>
                ) : (
                  <Link key={c.id} to={`/cereri/${c.id}`} style={s.row}>
                    <span style={{...s.td, flex: 1.5, fontFamily: 'monospace', fontSize: '12px', color: '#888'}}>{c.numar_cerere}</span>
                    <span style={{...s.td, flex: 4, color: '#1a1a2e', fontWeight: '500'}}>{truncate(c.titlu, 55)}</span>
                    <span style={{...s.td, flex: 1.5, fontSize: '12px', color: '#666'}}>{TIP_LABELS[c.tip] || c.tip}</span>
                    <span style={{...s.td, flex: 2}}><StatusBadge status={c.status} /></span>
                    <span style={{...s.td, flex: 2, fontSize: '12px', color: '#888'}}>{formatData(c.created_at)}</span>
                    {isFunctionar && <span style={{...s.td, flex: 2, fontSize: '12px', color: '#666'}}>{c.solicitant || '—'}</span>}
                  </Link>
                ))}

                {/* Paginare */}
                {pagination.pages > 1 && (
                  <div style={s.pagination}>
                    <button
                      onClick={() => setFilter('page', filters.page - 1)}
                      disabled={filters.page <= 1}
                      style={s.pageBtn}
                    >← Prev</button>
                    <span style={s.pageInfo}>
                      Pagina {filters.page} din {pagination.pages}
                    </span>
                    <button
                      onClick={() => setFilter('page', filters.page + 1)}
                      disabled={filters.page >= pagination.pages}
                      style={s.pageBtn}
                    >Urm →</button>
                  </div>
                )}
              </div>
            )
        }
        {isFetching && !isLoading && <div style={s.refreshing}>Se actualizează...</div>}
      </Card>
    </Layout>
  );
}

const s = {
  filterRow:   { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' },
  searchInput: { flex: 2, minWidth: '160px', padding: '9px 14px', fontSize: '16px', border: '1.5px solid #ddd', borderRadius: '8px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  select:      { padding: '9px 14px', fontSize: '16px', border: '1.5px solid #ddd', borderRadius: '8px', outline: 'none', backgroundColor: '#fff', WebkitAppearance: 'none' },
  clearBtn:    { padding: '9px 14px', background: 'none', border: '1.5px solid #ddd', borderRadius: '8px', cursor: 'pointer', color: '#888', fontSize: '13px' },
  tabs:        { display: 'flex', gap: '4px', marginBottom: '12px', flexWrap: 'wrap' },
  tab:         { padding: '6px 14px', border: '1px solid #ddd', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', backgroundColor: '#fff', color: '#555' },
  tabActive:   { backgroundColor: '#2d2d7e', color: '#fff', border: '1px solid #2d2d7e' },
  tableHeader: { display: 'flex', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', marginBottom: '4px' },
  th:          { fontSize: '12px', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' },
  row:         { display: 'flex', alignItems: 'center', padding: '12px', borderBottom: '1px solid #f5f5f5', textDecoration: 'none', transition: 'background .1s', cursor: 'pointer' },
  td:          { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '8px' },
  pagination:  { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', padding: '20px 0 4px' },
  pageBtn:     { padding: '8px 16px', border: '1.5px solid #ddd', borderRadius: '8px', cursor: 'pointer', backgroundColor: '#fff', fontSize: '13px' },
  pageInfo:    { fontSize: '13px', color: '#666' },
  refreshing:  { textAlign: 'center', fontSize: '12px', color: '#aaa', padding: '8px' },
  card:        { display: 'block', padding: '14px 12px', borderBottom: '1px solid #f0f0f0', textDecoration: 'none', color: 'inherit' },
  cardTop:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' },
  cardNumar:   { fontFamily: 'monospace', fontSize: '12px', color: '#888' },
  cardTitlu:   { fontSize: '15px', fontWeight: '500', color: '#1a1a2e', marginBottom: '6px' },
  cardMeta:    { display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#888' },
  cardSolicitant: { fontSize: '12px', color: '#666', marginTop: '4px' },
};
